// Sidebar nav. The 订单 / 排单 / Dashboard entries (dead external OMS links)
// were removed in the identity PR; 账号管理 now points at the in-app user
// management tab and is gated by the can_manage_users superuser flag.

import { useState, useEffect } from 'react';
import { Icon } from './Icon.jsx';
import { VavaLogo } from './VavaLogo.jsx';
import { Avatar } from './Avatar.jsx';
import { navigate } from '../lib/routing.js';
import { isElevated, canAccessHR, isCsManager } from '../lib/auth.ts';
import { NotificationBell } from './NotificationBell.jsx';
import * as api from '../lib/api.ts';

// Sidebar nav grouped by BUSINESS DOMAIN: 库存 (inventory) / 工作台 (work) /
// 系统 (system) — 库存 leads since the daily stock loop is the primary use.
// 我的待办 (the personal task list) leads the 工作台 group. The group header
// is 工作台 (not 待办) so the items don't echo "待办" — only 待办总览 carries it.
// Within each group, items run 日常 → 报表 → 设置.
// Section markers ({ kind:'section' }) render as non-clickable group labels.
//
// Per Regina's call: every tab open to all signed-in staff; only 账号管理
// stays gated (admin or can_manage_users — see requireUserMgmt).
// Every nav item has a UNIQUE icon so the mobile icon-only bottom bar stays
// legible (no two items share a glyph).
const SIDEBAR_NAV = [
  // ---- 库存 (inventory): 现货 → 报表 → 设置 → 活动日志 (放最后) ----
  { kind: 'section', label: '库存' },
  { key: 'order-entry', label: '备货录入',   icon: 'clipboard', href: '#/orders/entry', pendingMarker: true },
  { key: 'kitchen',     label: '后厨汇总',   icon: 'kitchen',   href: '#/orders/kitchen' },
  { key: 'inventory',   label: '当日现货',   icon: 'orders',    href: '#/inventory', certifyMarker: true },
  { key: 'inv-stats',   label: '统计报表',   icon: 'chart',     href: '#/inventory/stats' },
  { key: 'inv-waste',   label: '报废分析',   icon: 'trash',     href: '#/inventory/waste' },
  { key: 'inv-products',label: '管理产品',   icon: 'box',       href: '#/inventory/products' },
  { key: 'inv-thresh',  label: '低库存阈值', icon: 'gauge',     href: '#/inventory/thresholds' },
  { key: 'inv-takers',  label: '取走人管理', icon: 'sliders',   href: '#/admin/custom-takers', adminOnly: true },
  { key: 'inv-activity',label: '活动日志',   icon: 'log',       href: '#/inventory/activity' },
  // ---- 工作台 (work): 我的待办 → 总览 → 报表 → 设置 ----
  { kind: 'section', label: '工作台' },
  { key: 'mytasks',     label: '我的待办',   icon: 'tasks',     href: '#/my-tasks' },
  { key: 'overview',    label: '待办总览',   icon: 'dashboard', href: '#/admin' },
  { key: 'reports',     label: '月度报告',   icon: 'calendar',  href: '#/admin/reports' },
  { key: 'groups',      label: '分类管理',   icon: 'folder',    href: '#/admin/groups' },
  { key: 'templates',   label: '项目模板',   icon: 'schedule',  href: '#/admin/templates' },
  { key: 'hr',          label: '人事档案',   icon: 'idcard',    href: '#/admin/hr', hrAccess: true },
  // ---- 系统 (system) ----
  { kind: 'section', label: '系统' },
  { key: 'ops',         label: '运营',       icon: 'pulse',     href: '#/admin/ops' },
  { key: 'datareset',   label: '数据清零',   icon: 'reset',     href: '#/admin/data-reset', adminOnly: true },
  { key: 'trash',       label: '已删除',     icon: 'restore',   href: '#/admin/trash', adminOnly: true },
  { key: 'users',       label: '账号管理',   icon: 'users',     href: '#/admin/users', userMgmt: true }
];

function activeKey(current) {
  if (current === '/admin/ops') return 'ops';
  if (current === '/admin/groups') return 'groups';
  if (current === '/admin/templates') return 'templates';
  if (current === '/admin/reports') return 'reports';
  if (current === '/admin/users') return 'users';
  if (current === '/admin/data-reset') return 'datareset';
  if (current === '/admin/trash') return 'trash';
  if (current === '/admin/custom-takers') return 'inv-takers';
  if (current === '/admin/hr' || current.startsWith('/admin/hr/')) return 'hr';
  // Inventory sub-pages: each gets its own highlight; product detail
  // pages fall back to the products tab.
  if (current === '/inventory/stats')      return 'inv-stats';
  if (current === '/inventory/waste')      return 'inv-waste';
  if (current === '/inventory/activity')   return 'inv-activity';
  if (current === '/inventory/thresholds') return 'inv-thresh';
  if (current === '/inventory/products' || current.startsWith('/inventory/products/')) return 'inv-products';
  if (current === '/inventory' || current.startsWith('/inventory/')) return 'inventory';
  if (current === '/orders/entry') return 'order-entry';
  if (current === '/orders/kitchen' || current.startsWith('/orders')) return 'kitchen';
  if (current === '/admin' || current === '/admin/'
      || current.startsWith('/people/') || current.startsWith('/admin/staff/')
      || current.startsWith('/admin/people/') || current.startsWith('/group/')) return 'overview';
  if (current.startsWith('/task/') || current.startsWith('/my-tasks')) return 'mytasks';
  return null;
}

// Live-sync indicator. Green = connected, amber = reconnecting, gray = offline.
// The last-sync time reassures staff the numbers on screen are fresh (so they
// don't act on a silently-frozen page when store wifi drops). Re-renders each
// minute so "刚刚 → N分钟前" ages without a data change.
function ConnStatus({ status, lastSync }) {
  const [, tick] = useState(0);
  useEffect(() => { const iv = setInterval(() => tick(n => n + 1), 60000); return () => clearInterval(iv); }, []);
  const ago = (() => {
    if (!lastSync) return '';
    const s = Math.floor((Date.now() - lastSync) / 1000);
    if (s < 60) return '刚刚同步';
    const m = Math.floor(s / 60);
    if (m < 60) return `${m} 分钟前同步`;
    return `${Math.floor(m / 60)} 小时前同步`;
  })();
  const label = status === 'online' ? (ago || '实时同步') : status === 'connecting' ? '重连中…' : '已离线';
  return (
    <div className={`conn-status conn-${status}`} role="status" title={status === 'online' ? '实时同步已连接' : status === 'connecting' ? '正在重新连接实时同步' : '实时同步已断开，界面数据可能不是最新'}>
      <span className="conn-dot" aria-hidden="true" />
      <span className="conn-label">{label}</span>
    </div>
  );
}

export const Sidebar = ({ user, onLogout, current, wsStatus = 'connecting', lastSync = null }) => {
  // "Elevated" = admin or 店长 — both see admin-tier tabs.
  const isAdmin = isElevated(user);
  const isSuper = !!user?.superAdmin;            // 账号管理: super-admin (Regina) full powers
  const isCs = isCsManager(user);                // 账号管理: 客服管理员 (scoped to 客服 staff)
  const isUserMgr = user?.role === 'admin' || isSuper || isCs;   // 账号管理: 管理员 / 超管 / 客服管理员
  const canHr = canAccessHR(user);               // 人事档案: admin / 店长 / 领班
  const active = activeKey(current);
  // Amber marker on 备货录入 when tomorrow's prep isn't 录入完成 yet, and on
  // 库存认证 when any past production day is still uncertified.
  const [orderPending, setOrderPending] = useState(false);
  const [certifyPending, setCertifyPending] = useState(false);
  useEffect(() => {
    if (!isElevated(user)) return;
    api.loadPendingConfirm().then(r => setOrderPending(!!r.pending)).catch(() => {});
    api.loadCertifyPending().then(r => setCertifyPending((r.count || 0) > 0)).catch(() => {});
  }, [user]);
  return (
    <aside className="vava-sidebar" aria-label="主导航">
      <div className="vava-brand">
        <div className="vava-brand-logo"><VavaLogo /></div>
        <div className="vava-brand-text">
          <div className="vava-brand-word">vava</div>
          <div className="vava-brand-sub">designer cakes</div>
        </div>
        <NotificationBell />
      </div>
      <nav className="vava-nav" aria-label="页面导航">
        {SIDEBAR_NAV
          .filter(item => (!item.adminOnly || isAdmin) && (!item.superOnly || isSuper) && (!item.userMgmt || isUserMgr) && (!item.hrAccess || canHr))
          .map((item, i) => {
            if (item.kind === 'section') {
              // Non-interactive group label. role=heading so screen readers
              // announce the grouping; hidden on mobile (icon-only bar).
              return (
                <div key={`section-${i}`} className="vava-nav-section"
                     role="heading" aria-level="2">
                  {item.label}
                </div>
              );
            }
            const classes = ['vava-nav-item'];
            if (active === item.key) classes.push('active');
            if (item.disabled) classes.push('disabled');
            const props = {
              className: classes.join(' '),
              'aria-current': active === item.key ? 'page' : undefined
            };
            if (item.disabled) {
              return (
                <button key={item.key} {...props} aria-disabled="true"
                        title="即将上线" disabled>
                  <Icon name={item.icon} />
                  <span>{item.label}</span>
                </button>
              );
            }
            return (
              <button key={item.key} {...props}
                      onClick={() => navigate(item.href.replace('#', ''))}>
                <Icon name={item.icon} />
                <span>{item.label}</span>
                {item.pendingMarker && orderPending && <span className="nav-pending-dot" title="明日备货未录入完成" />}
                {item.certifyMarker && certifyPending && <span className="nav-pending-dot" title="有未认证的库存日" />}
              </button>
            );
          })}
      </nav>
      <div className="vava-sidebar-footer">
        <ConnStatus status={wsStatus} lastSync={lastSync} />
        <button type="button" title="查看个人资料 · 修改密码 · 上传头像"
                onClick={() => navigate('/profile')}
                style={{ background: 'none', border: 'none', padding: 0,
                         display: 'flex', alignItems: 'center', gap: 10,
                         cursor: 'pointer', textAlign: 'left',
                         font: 'inherit', color: 'inherit', width: '100%' }}>
          <Avatar staff={user} large />
          <span style={{ minWidth: 0, flex: 1 }}>
            <span className="name" style={{ display: 'block', overflow: 'hidden',
                                            textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user?.name}
            </span>
            <span style={{ display: 'block', fontSize: 11,
                           color: 'var(--vava-text-muted)', overflow: 'hidden',
                           textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user?.title || ''}
            </span>
          </span>
        </button>
        <a href="#" className="logout" onClick={(e) => { e.preventDefault(); onLogout(); }}>退出 →</a>
      </div>
    </aside>
  );
};
